<template>
  <div>
    <b-modal
      id="refund-amount"
      hide-footer
      @hidden="modalHiddenHandler"
    >
      <template #modal-title>
        <span
          v-if="activeCustomRefundRow"
        >Visa Amount : <span class="text-danger">{{ activeCustomRefundRow.amount }}</span>
        </span>
      </template>
      <b-form-group
        label="Refund To"
        label-for="refund-to"
        class="mb-1"
      >
        <b-form-radio-group
          id="refund-to"
          v-model="refundTo"
          :options="refundToOptions"
        />
      </b-form-group>
      <b-form-group
        label="Refund Type"
        label-for="refund-type"
        class="mb-1"
      >
        <b-form-radio-group
          id="refund-type"
          v-model="refundType"
          :options="refundOptions"
        />
      </b-form-group>
      <b-form-input
        v-if="refundType == REFUND_TYPES.CUSTOM"
        v-model.number="customRefundAmount"
        type="number"
        placeholder="Enter Custom Amount"
        class="my-1"
      />
      <b-button
        block
        :disabled="refundingAmount"
        variant="primary"
        @click="refundAmount"
      >
        <b-spinner
          v-if="refundingAmount"
          small
        />
        <span v-else>Apply Refund</span>
      </b-button>
    </b-modal>
    <b-overlay
      variant="white"
      spinner-variant="primary"
      blur="0"
      opacity="0.75"
      rounded="sm"
      :show="loadingVisaTransactions"
    >
      <b-card>
        <div class="d-flex justify-content-between align-items-center">
          <b-card-title>Visa Transactions History</b-card-title>
          <b-button
            class="btn-icon rounded-circle ml-1"
            variant="flat-primary"
            @click="refreshVisaTransactions"
          >
            <feather-icon icon="RefreshCwIcon" />
          </b-button>
        </div>

        <b-table
          primary-key="id"
          responsive
          :fields="tableColumns"
          :items="tableData"
          show-empty
          empty-text="No records found"
          class="mt-2"
          :per-page="perPage"
          :current-page="page"
        >
          <template #cell(created_at)="data">
            {{ data.item.created_at | formatDate }}
          </template>
          <template #cell(type)>
            Visa
          </template>
          <template #cell(state)="data">
            <b-badge :variant="stateVariant(data.item.state)">
              {{ snakeCaseToCamcelCaase(data.item.state) }}
            </b-badge>
          </template>
          <template #cell(tripDetails)="data">
            <b-spinner
              v-if="data.item.isLoading"
              variant="primary"
            />
            <b-button
              v-else
              class="btn-icon rounded-circle ml-1"
              variant="flat-primary"
              @click="redirectToTripDetails(data.item)"
            >
              <feather-icon
                icon="TruckIcon"
                size="18"
              />
            </b-button>
          </template>
          <template
            #cell(actions)="data"
          >
            <b-dropdown
              v-if="data.item.state === TransactionStateEnum.SUCCEEDED || data.item.state === TransactionStateEnum.PENDING"
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <b-spinner
                  v-if="data.item.loadingRefund"
                  variant="primary"
                  small
                />
                <feather-icon
                  v-else
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item
                v-if="data.item.state === TransactionStateEnum.SUCCEEDED"
                :disabled="data.item.state === TransactionStateEnum.REFUNDED || data.item.state === TransactionStateEnum.REFUND_PENDING"
                @click="openRefundModal(data.item)"
              >
                Refund
              </b-dropdown-item>
              <b-dropdown-item
                v-if="data.item.state === TransactionStateEnum.PENDING"
                @click="checkStatus(data.item)"
              >Check Status
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <base-pagination
          v-model="page"
          :total="total"
          @new-per-page="perPage = $event"
        />
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardTitle, BTable, BOverlay, BBadge, BButton, BSpinner, BDropdown,
  BFormGroup,
  BDropdownItem, BFormInput, BModal, BFormRadioGroup,
} from 'bootstrap-vue'
import { computed, onUnmounted, ref } from '@vue/composition-api'
import BasePagination from '@/UI/BasePagination.vue'
import snakeCaseToCamcelCaase from '@/utils/snake-case-to-camcel-caase'
import { errorNotification, successNotification } from '@/utils/toast-notifications'
import axiosIns from '@/libs/axios'

const TransactionStateEnum = {
  PENDING: 'PENDING',
  FAILED: 'FAILED',
  SUCCEEDED: 'SUCCEEDED',
  REFUND_PENDING: 'REFUND_PENDING',
  REFUNDED: 'REFUNDED',
}

const REFUND_TYPES = {
  FIXED: 'Fixed',
  CUSTOM: 'Custom',
}

const REFUND_OPTIONS = {
  BANK: 'Bank',
  WALLET: 'Wallet',
}
export default {
  components: {
    BCard,
    BModal,
    BFormGroup,
    BCardTitle,
    BTable,
    BOverlay,
    BasePagination,
    BFormInput,
    BBadge,
    BButton,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BFormRadioGroup,
  },
  props: {
    loadingVisaTransactions: {
      type: Boolean,
      default: false,
    },
    visaTransactionsData: {
      type: Object,
      default: () => { },
    },
  },
  setup(props, ctx) {
    const refundType = ref(REFUND_TYPES.FIXED)
    const refundOptions = [REFUND_TYPES.FIXED, REFUND_TYPES.CUSTOM]

    const refundTo = ref(REFUND_OPTIONS.WALLET)
    const refundToOptions = [REFUND_OPTIONS.BANK, REFUND_OPTIONS.WALLET]

    onUnmounted(() => {
      ctx.root.$store.commit('users/SET_USER_VISA_TRANSACTIONS', null)
    })
    const tableColumns = [
      { key: 'created_at', label: 'DATE/TIME' },
      { key: 'type', label: 'Type' },
      { key: 'amount', label: 'Amount' },
      { key: 'state', label: 'State' },
      (ctx.root.$can('write', 'visa-transactions') && { key: 'actions', label: 'Actions' }),
      { key: 'meta', label: 'reason', formatter: val => val?.reason },
      { key: 'tripDetails', label: '' },
    ]
    const total = ref(null)
    const page = ref(1)
    const perPage = ref(10)
    // eslint-disable-next-line consistent-return
    const tableData = computed(() => {
      if (props.visaTransactionsData) {
        total.value = props.visaTransactionsData.data.length
        return props.visaTransactionsData.data.map(el => ({
          ...el,
          isLoading: false,
          loadingRefund: false,
        }))
      }
      return []
    })
    const stateVariant = state => {
      if (state === TransactionStateEnum.FAILED) {
        return 'danger'
      } if (state === TransactionStateEnum.SUCCEEDED || state === TransactionStateEnum.REFUNDED) {
        return 'success'
      }
      return 'warning'
    }
    const redirectToTripDetails = async item => {
      try {
        // eslint-disable-next-line no-param-reassign
        item.isLoading = true
        const res = await ctx.root.$store.dispatch('billing/fetchTripIdByBillId', item.bill_id)
        const { external_id: externalId } = res.data.data
        ctx.root.$router.push(`/trips/${externalId}`)
      } catch (error) {
        console.log(error)
      } finally {
        // eslint-disable-next-line no-param-reassign
        item.isLoading = false
      }
    }
    const refreshVisaTransactions = () => {
      ctx.emit('fetch-visa-transactions')
    }
    const checkStatus = async row => {
      const paymentId = row?.meta?.initial_response?.payment_id
      if (paymentId) {
        const result = await axiosIns.post('payment-status-montypay', {
          payment_id: paymentId,
        })
        const { status } = result.data.data.data
        if (status === 'settled') {
          // eslint-disable-next-line no-param-reassign
          row.state = TransactionStateEnum.SUCCEEDED
        }
      } else {
        errorNotification('Payment ID not found..')
      }
    }

    const activeCustomRefundRow = ref()
    const openRefundModal = row => {
      activeCustomRefundRow.value = JSON.parse(JSON.stringify(row))

      ctx.root.$bvModal.show('refund-amount')
    }

    const customRefundAmount = ref()

    const refundingAmount = ref(false)
    const refundAmount = async () => {
      try {
        if (refundType.value === REFUND_TYPES.CUSTOM) {
          if (+customRefundAmount.value > activeCustomRefundRow.value.amount) {
            errorNotification('Custom amount is larger than actual amount')
            return
          }
        }
        refundingAmount.value = true
        await ctx.root.$store.dispatch('billing/refund', {
          custom_amount: refundType.value === REFUND_TYPES.CUSTOM ? customRefundAmount.value : undefined,
          target: refundTo.value === REFUND_OPTIONS.BANK ? 'bank' : 'wallet',
          payment_transaction_id: activeCustomRefundRow.value.id,
        })
        ctx.emit('fetch-wallet')
        ctx.root.$bvModal.hide('refund-amount')
        successNotification('success..')
      } catch (err) {
        errorNotification('An error occured')
      } finally {
        refundingAmount.value = false
      }
    }

    const modalHiddenHandler = () => {
      refundType.value = REFUND_TYPES.FIXED
      refundTo.value = REFUND_OPTIONS.WALLET
      customRefundAmount.value = null
    }

    return {
      tableColumns,
      customRefundAmount,
      tableData,
      total,
      page,
      openRefundModal,
      refundAmount,
      modalHiddenHandler,
      refundType,
      checkStatus,
      perPage,
      snakeCaseToCamcelCaase,
      refundingAmount,
      refundOptions,
      stateVariant,
      refundTo,
      refundToOptions,
      redirectToTripDetails,
      refreshVisaTransactions,
      activeCustomRefundRow,
      TransactionStateEnum,
      REFUND_TYPES,
      successNotification,
    }
  },
}
</script>
